/**
 * All string literal union types used as enums throughout the platform.
 * Import from here - never use magic strings elsewhere.
 */

export const TIERS = ['free', 'pro', 'agency'] as const
export type Tier = (typeof TIERS)[number]

export const AI_MODELS = [
  // Anthropic
  'claude-haiku',
  'claude-sonnet',
  'claude-opus',
  // OpenAI
  'gpt-4o-mini',
  'gpt-4.1',
  // Google
  'gemini-2.5-flash',
  'gemini-2.5-pro',
  // Perplexity
  'perplexity-sonar',
  'perplexity-sonar-pro',
] as const
export type AIModel = (typeof AI_MODELS)[number]

export type AIProvider = 'anthropic' | 'openai' | 'google' | 'perplexity'

/** Models currently available for selection. All others are "coming soon". */
export const ENABLED_MODELS = new Set<AIModel>(['claude-haiku', 'claude-sonnet'])

/** Credit cost per prompt-model check. 1 credit ≈ $0.01. */
export const MODEL_CREDIT_COST: Record<AIModel, number> = {
  'gemini-2.5-flash':     1,
  'gpt-4o-mini':          1,
  'claude-haiku':         2,
  'gemini-2.5-pro':       5,
  'perplexity-sonar':     8,
  'claude-sonnet':        10,
  'gpt-4.1':              10,
  'perplexity-sonar-pro': 15,
  'claude-opus':          20,
}

/** Monthly credit allotment by tier */
export const TIER_MONTHLY_CREDITS: Record<Tier, number> = {
  free:   10,
  pro:    1_000,
  agency: 5_000,
}

export const SENTIMENTS = ['positive', 'neutral', 'negative'] as const
export type Sentiment = (typeof SENTIMENTS)[number]

export const CHECK_INTERVALS = ['once', 'weekly', 'daily', 'custom'] as const
export type CheckInterval = (typeof CHECK_INTERVALS)[number]

export const JOB_STATUSES = ['pending', 'queued', 'running', 'done', 'partial', 'failed', 'expired'] as const
export type JobStatus = (typeof JOB_STATUSES)[number]

export const ALERT_TYPES = ['email', 'webhook'] as const
export type AlertType = (typeof ALERT_TYPES)[number]

/** Maximum brands per tier */
export const TIER_BRAND_LIMITS: Record<Tier, number> = {
  free: 1,
  pro: 5,
  agency: Infinity,
}

/** Maximum prompts (keywords) per brand per tier */
export const TIER_PROMPT_LIMITS: Record<Tier, number> = {
  free: 3,
  pro: 50,
  agency: Infinity,
}

/** Maximum AI models per brand per tier */
export const TIER_MODEL_LIMITS: Record<Tier, number> = {
  free: 1,
  pro: 4,
  agency: 4,
}

/** Allowed check intervals per tier */
export const TIER_INTERVAL_ALLOWLIST: Record<Tier, CheckInterval[]> = {
  free: ['once', 'weekly'],
  pro: ['once', 'weekly', 'daily'],
  agency: ['once', 'weekly', 'daily', 'custom'],
}

/** Maximum prompt text length in characters */
export const PROMPT_MAX_LENGTH = 500

export const OAUTH_PROVIDERS = ['github', 'google'] as const
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number]
